/*
  Variant based bundle selector.

  Twin of the quantity breaks bundle, except every option is a real variant. Picking an
  option swaps the variant on the product form, the displayed price, the sticky add to
  cart bar and the featured media, without re-rendering the section (so the chosen
  option never flickers back).
*/
if (!customElements.get('variant-breaks')) {
  class VariantBreaks extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.sectionId = this.dataset.section;
      this.formId = this.dataset.formId;
      this.shouldUpdateUrl = this.dataset.updateUrl !== 'false';

      this.addEventListener('change', this.onChange.bind(this));

      const checked = this.querySelector('input[type="radio"]:checked');
      if (checked) this.select(checked, false);
    }

    onChange(event) {
      const input = event.target;
      if (!input || input.type !== 'radio') return;
      this.select(input, true);
    }

    select(input, userInitiated) {
      const variantId = input.value;

      this.updateVariantInput(variantId);
      this.updatePrice(input);
      this.updateButtons(input);
      this.updateSelectedVariantText(input);
      this.updateMedia(input);

      if (userInitiated && this.shouldUpdateUrl && this.dataset.url) {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${variantId}`);
      }
    }

    /* ------------------------------------------------------------------ form */

    getForms() {
      return [
        document.getElementById(this.formId),
        document.getElementById(`product-form-installment-${this.sectionId}`),
      ].filter(Boolean);
    }

    updateVariantInput(variantId) {
      this.getForms().forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variantId;
        input.disabled = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /* ----------------------------------------------------------------- price */

    // The money strings are taken from the option that was just picked, so they keep
    // whatever currency formatting the theme rendered them with.
    getMoney(label, selector) {
      const element = label && label.querySelector(selector);
      if (!element || element.classList.contains('hidden')) return '';
      const money = element.querySelector('span');
      return (money ? money.textContent : element.textContent).trim();
    }

    updatePrice(input) {
      const label = this.querySelector(`label[for="${input.id}"]`);
      if (!label) return;

      const price = this.getMoney(label, '.quantity-break__price');
      const comparePrice = this.getMoney(label, '.quantity-break__compare-price');
      if (!price) return;

      const targets = [
        document.getElementById(`price-${this.sectionId}`),
        document.getElementById(`price-mobile-${this.sectionId}`),
        ...document.querySelectorAll('.product-form.sticky-atc'),
      ].filter(Boolean);

      targets.forEach((target) => this.setPrice(target, price, comparePrice));
    }

    setPrice(target, price, comparePrice) {
      const onSale = Boolean(comparePrice) && comparePrice !== price;

      target.querySelectorAll('.price').forEach((element) => {
        element.classList.toggle('price--on-sale', onSale);
      });

      target.querySelectorAll('.price__regular .price-item--regular').forEach((element) => {
        element.textContent = price;
      });
      target.querySelectorAll('.price__sale .price-item--regular').forEach((element) => {
        element.textContent = onSale ? comparePrice : price;
      });
      target.querySelectorAll('.price__sale .price-item--sale').forEach((element) => {
        element.textContent = price;
      });
    }

    /* --------------------------------------------------------------- buttons */

    getButtons() {
      const buttons = new Set();

      this.getForms().forEach((form) => {
        form.querySelectorAll('[name="add"]').forEach((button) => buttons.add(button));
      });
      document
        .querySelectorAll(`[name="add"][form="${this.formId}"]`)
        .forEach((button) => buttons.add(button));

      return Array.from(buttons);
    }

    updateButtons(input) {
      const available = input.dataset.available === 'true';
      const strings = window.variantStrings || {};

      this.getButtons().forEach((button) => {
        if (!button.dataset.defaultLabel) {
          // Only trust the rendered label when the button starts out enabled, otherwise
          // "Sold out" would be remembered as the add to cart wording.
          const rendered = this.getButtonLabel(button);
          const soldOutOnLoad = button.hasAttribute('disabled');
          button.dataset.defaultLabel = soldOutOnLoad ? strings.addToCart || rendered : rendered;
        }

        if (available) {
          button.removeAttribute('disabled');
          this.setButtonLabel(button, button.dataset.defaultLabel);
        } else {
          button.setAttribute('disabled', 'disabled');
          this.setButtonLabel(button, strings.soldOut || button.dataset.defaultLabel);
        }
      });
    }

    getButtonTextNode(button) {
      const target = button.querySelector('.atc-text') || button.querySelector('span');
      if (!target) return null;
      return Array.from(target.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    }

    getButtonLabel(button) {
      const node = this.getButtonTextNode(button);
      return node ? node.textContent.trim() : '';
    }

    // Only the text node is replaced so the arrow icon inside the button survives.
    setButtonLabel(button, text) {
      if (!text) return;
      const node = this.getButtonTextNode(button);
      if (node) node.textContent = text;
    }

    /* ----------------------------------------------------------------- extra */

    updateSelectedVariantText(input) {
      const title = input.dataset.variantTitle;
      if (!title) return;

      document.querySelectorAll('.sticky-atc-active-variant').forEach((container) => {
        const values = container.querySelectorAll('.sticky-atc-active-variant__option-value');
        if (values.length === 1) values[0].textContent = title;
      });
    }

    updateMedia(input) {
      const mediaId = input.dataset.mediaId;
      if (!mediaId) return;

      document.querySelectorAll(`[id^="MediaGallery-${this.sectionId}"]`).forEach((gallery) => {
        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(`${this.sectionId}-${mediaId}`, true);
        }
      });
    }
  }

  customElements.define('variant-breaks', VariantBreaks);
}
